/**
 * CoachingSessionCard
 * Displays a coaching session card matching Figma "Cards / Session Paid + Stats"
 * 640x468px, cover image with rounded corners, price badge, stats bar at bottom
 */

import {
  Banknote,
  Calendar,
  CircleCheck,
  Eye,
  MoreVertical,
  Pencil,
  Rocket,
  Trash2,
  Upload,
  Users,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { BookmarkFilledIcon, BookmarkIcon } from "@/components/icons"
import { ShareModal } from "@/components/shared/ShareModal"
import { TagChip } from "@/components/shared/TagChip"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { CoachingBookSessionModal } from "@/features/coaching/components/CoachingBookSessionModal"
import { incrementCoachingViewCount } from "@/features/coaching/services/coaching.service"
import { formatDuration } from "@/features/rituals/utils/formatDuration"

export interface CoachingSessionCardProps {
  id: string
  slug?: string
  title: string
  coverImage?: string | null
  format: string
  duration: number
  durationType?: "minutes" | "hours" | "days"
  participationFee: string
  price?: number | null
  description?: string | null
  tags?: string[]
  location?: string | null
  websiteLink?: string | null
  creator?: {
    id: string
    slug?: string
    name: string | null
    username: string | null
    profilePictureUrl?: string | null
    followers?: number
  } | null
  isOwner?: boolean
  isBookmarked?: boolean
  /** Flag to indicate if this is a draft session */
  isDraft?: boolean
  /** When true, the card shows a "Paused" badge instead of price and hides the Book button */
  isPaused?: boolean
  onBookmarkClick?: () => void
  onEditClick?: () => void
  onDeleteClick?: () => void
  onCalendarClick?: () => void
  onBookSession?: () => void
  /** Callback when clicking the Publish action (for draft cards) */
  onPublishClick?: () => void
  /** Callback when clicking the Bookings stat - opens bookings modal */
  onBookingsClick?: (sessionData: {
    id: string
    title: string
    coverImage?: string | null
    participationFee: string
    price: number | null | undefined
    duration: number
    format: string
  }) => void
  /** Callback when clicking the Completed stat - opens bookings modal with completed tab */
  onCompletedClick?: (sessionData: {
    id: string
    title: string
    coverImage?: string | null
    participationFee: string
    price: number | null | undefined
    duration: number
    format: string
  }) => void
  stats?: {
    views: number
    bookings: number
    completed: number
    earning: number
  } | null
}

export function CoachingSessionCard({
  id,
  slug,
  title,
  coverImage,
  format,
  duration,
  durationType = "minutes",
  participationFee,
  price,
  description,
  tags,
  creator,
  isOwner = false,
  isBookmarked,
  isDraft = false,
  isPaused = false,
  onBookmarkClick,
  onEditClick,
  onDeleteClick,
  onCalendarClick,
  onBookSession,
  onPublishClick,
  onBookingsClick,
  onCompletedClick,
  location,
  websiteLink,
  stats,
}: CoachingSessionCardProps) {
  const navigate = useNavigate()
  const currentUser = useAuthStore(state => state.user)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  const isPaid = participationFee === "Paid"
  const priceNum = price ? Number.parseFloat(String(price)) : 0
  const priceLabel = isPaid && priceNum > 0 ? `$${Math.round(priceNum)}` : "Free"
  const durationLabel = formatDuration(duration, durationType)
  // Drafts are never paused, regardless of the isPaused prop
  const showPaused = isPaused && !isDraft

  const handleBookSessionClick = async () => {
    // Increment view count when Book a Session is clicked
    try {
      await incrementCoachingViewCount(id)
    } catch (error) {
      // Silently fail - view count is not critical for user experience
      console.warn("Failed to increment view count:", error)
    }

    if (onBookSession) {
      onBookSession()
    } else {
      // If no callback provided, open the modal directly
      setIsBookingModalOpen(true)
    }
  }

  return (
    <div className="relative max-w-[640px] h-full w-full overflow-hidden rounded-tl-[30px] rounded-br-[30px] md:rounded-tl-[40px] md:rounded-br-[40px] border border-white/10 bg-white/5 flex flex-col">
      {/* Cover Image */}
      <div className="relative h-[220px] md:h-[300px] w-full overflow-hidden rounded-tl-[30px] md:rounded-tl-[40px] bg-brand-slate shrink-0">
        {coverImage ? (
          <img
            src={coverImage}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover z-0"
          />
        ) : (
          <div className="absolute inset-0 bg-brand-slate z-0" />
        )}
        {/* Dark overlay — between image and buttons, only when paused */}
        {showPaused && (
          <div
            className="absolute inset-0 z-[1] pointer-events-none"
            style={{ background: "#020202", opacity: 0.8 }}
          />
        )}
        <div className="absolute right-5 top-5 flex items-center gap-[10px] z-[2]">
          {/* Admin Controls */}
          {isOwner && (
            <>
              {/* Calendar View Button */}
              {onCalendarClick && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    onCalendarClick()
                  }}
                  className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
                  aria-label="Open calendar"
                >
                  <Calendar className="w-[22px] h-[22px] text-primary" />
                </button>
              )}

              {/* Share Button — disabled when paused */}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  setShareOpen(true)
                }}
                className="shrink-0 rounded-[10px] border-gradient-glass p-2.75 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Share"
              >
                <Upload className="w-[22px] h-[22px] text-primary" />
              </button>

              {/* Three-dot Dropdown Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 cursor-pointer rounded-[10px] border-gradient-glass p-2.75 transition-colors "
                    aria-label="More options"
                  >
                    <MoreVertical className="w-[22px] h-[22px] text-primary" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-white/50 backdrop-blur-sm border-white/20"
                >
                  {onPublishClick && isDraft && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onPublishClick()
                      }}
                      className="text-black cursor-pointer"
                    >
                      <Rocket className="w-4 h-4 mr-2" />
                      Publish
                    </DropdownMenuItem>
                  )}
                  {onEditClick && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onEditClick()
                      }}
                      className="text-[#000000] cursor-pointer"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {onDeleteClick && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onDeleteClick()
                      }}
                      className="text-[#000000] cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Bookmark Button */}
          {onBookmarkClick && !isOwner && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                if (!currentUser) {
                  setShowLoginModal(true)
                  return
                }
                onBookmarkClick()
              }}
              className="shrink-0 cursor-pointer rounded-[10px] border-gradient-glass p-2.75 transition-colors"
              aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              {isBookmarked ? (
                <BookmarkFilledIcon className="w-[22px] h-[22px] fill-white text-white" />
              ) : (
                <BookmarkIcon className="w-[22px] h-[22px] text-primary" />
              )}
            </button>
          )}
        </div>

        {/* Live Glass Circle - centered on cover, only shown when coaching is paused (Figma: 70x70 glass circle) */}
        {showPaused && (
          <div className="absolute inset-0 flex items-center justify-center z-[2] pointer-events-none">
            <div className="w-[70px] h-[70px] rounded-full border border-gradient-glass backdrop-blur-sm flex items-center justify-center pointer-events-auto">
              <svg width="26" height="26" viewBox="0 0 29 29" fill="none" className="text-primary">
                <path
                  d="M1.81381 0.311207C1.39888 -0.10373 0.726143 -0.103736 0.311207 0.311191C-0.10373 0.726119 -0.103736 1.39886 0.311191 1.81379L3.42574 4.92841C-1.37156 10.4941 -1.13037 18.9043 4.14932 24.184C4.56425 24.599 5.23699 24.599 5.65192 24.184C6.06686 23.7691 6.06686 23.0964 5.65192 22.6814C1.20254 18.232 0.96299 11.167 4.93327 6.43597L7.4514 8.95415C4.85631 12.2887 5.09126 17.1121 8.15626 20.1771C8.57119 20.592 9.24393 20.592 9.65886 20.1771C10.0738 19.7622 10.0738 19.0894 9.65886 18.6745C7.42528 16.4409 7.19559 12.9622 8.96982 10.4726L12.1136 13.6164C12.0667 13.7919 12.0417 13.9764 12.0417 14.1667C12.0417 15.3403 12.9931 16.2917 14.1667 16.2917C14.3569 16.2917 14.5414 16.2667 14.7169 16.2198L26.5192 28.0223C26.9341 28.4373 27.6069 28.4373 28.0218 28.0224C28.4367 27.6074 28.4368 26.9347 28.0218 26.5198L1.81381 0.311207ZM24.6337 20.1263L26.1832 21.6758C29.6079 16.2062 28.9415 8.90683 24.184 4.14933C23.7691 3.7344 23.0963 3.7344 22.6814 4.14933C22.2665 4.56426 22.2665 5.237 22.6814 5.65193C26.6038 9.5743 27.2545 15.5294 24.6337 20.1263ZM20.3252 15.8178L21.994 17.4865C23.3052 14.3922 22.6995 10.6787 20.1771 8.15627C19.7621 7.74134 19.0894 7.74134 18.6745 8.15627C18.2595 8.5712 18.2595 9.24394 18.6745 9.65887C20.3431 11.3275 20.8933 13.6909 20.3252 15.8178Z"
                  fill="#F2F0EB"
                />
              </svg>{" "}
            </div>
          </div>
        )}
      </div>

      <div className="p-3.75 md:p-5 flex flex-col flex-1 min-h-0">
        {/* Title */}
        <h3 className="text-sm md:text-[18px] font-semibold leading-5.5 text-[#F2F0EB] line-clamp-1">
          {title}
        </h3>
        <div className="flex items-center text-xs md:text-[13px] gap-2.5 mt-2.5">
          {showPaused ? (
            <span className="rounded-[5px] px-1.5 py-0.5 text-[13px] leading-5 bg-brand-slate text-[#F2F0EB]/90">
              Paused
            </span>
          ) : (
            <span
              className={`rounded-[5px] px-1.5 py-0.5 leading-5 text-connect-cream opacity-90 ${
                isPaid ? "bg-brand-terracotta" : "bg-brand-olive"
              }`}
            >
              {priceLabel}
            </span>
          )}
          <span className="leading-5 text-[#F2F0EB]">Private Session</span>
          <span className="leading-normal text-[#F2F0EB]">{durationLabel}</span>
        </div>
        {/* Creator Profile */}
        {creator && !isOwner && (
          <div className="flex items-center gap-2.5 my-5">
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                navigate(`/profile/${creator.id}`)
              }}
              className="cursor-pointer rounded-full border border-white/20"
              aria-label={`View ${creator.name || creator.username}'s profile`}
            >
              <Avatar
                src={creator.profilePictureUrl ?? undefined}
                fallback={creator.name || creator.username || "?"}
                size="sm"
                className="w-7.5 h-7.5"
              />
            </button>
            <span className="text-sm font-semibold text-[#F2F0EB] leading-5.5">
              {creator.name || creator.username}
            </span>
            <span className="text-sm text-[#F2F0EB80] leading-5.5">
              {(creator.followers || 0) >= 1000
                ? `${((creator.followers || 0) / 1000).toFixed(1)}k`
                : creator.followers || 0}{" "}
              follower{(creator.followers || 0) !== 1 && "s"}
            </span>
          </div>
        )}
        {/* Description */}
        {description && !isOwner && (
          <p className="text-[13px] leading-5 text-[#F2F0EB] line-clamp-2 overflow-hidden">
            {description}
          </p>
        )}
        <div className="flex lg:flex-row flex-col justify-between lg:items-center gap-1 mt-auto">
          {/* Tags Row */}
          {!isOwner && (
            <div className="flex items-center overflow-x-auto gap-2.5 pt-5 lg:max-w-70">
              {tags && tags.length > 0 && (
                <>
                  {tags.slice(0, 3).map(tag => (
                    <TagChip
                      key={tag}
                      tag={tag}
                      tab="sessions"
                      className="border border-[#F2F0EB] rounded-lg p-2.5 text-[13px] leading-5 text-[#F2F0EB]"
                    />
                  ))}
                  {tags?.length > 3 && (
                    <span className="text-sm text-[#F2F0EB]">+{tags?.length - 3}</span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Book a Session Button (non-owner view, hidden when paused) */}
          {!isOwner && !showPaused && (
            <Button
              variant="outline"
              type="button"
              onClick={e => {
                e.stopPropagation()
                if (!currentUser) {
                  setShowLoginModal(true)
                  return
                }
                handleBookSessionClick()
              }}
              className="mt-4 h-[50px] w-full py-[15.5px] bg-transparent rounded-full flex items-center justify-center gap-2.5 transition-colors cursor-pointer text-[#F2F0EB] hover:text-[#000] border-[#F2F0EB] lg:max-w-[230px] w-full"
            >
              <Calendar className="w-4.5 h-4.5" />
              <span className="text-sm font-semibold uppercase">Book a Session</span>
            </Button>
          )}
        </div>
      </div>

      {/* Owner stats matching Figma: 16px font, 20px gap, semibold numbers, 60% opacity */}
      {isOwner && (
        <div className="px-5 py-5 bg-[#F2F0EB]/10 shrink-0 hidden md:block">
          <div className="text-[16px] leading-[20px] text-[#F2F0EB]/60 flex flex-wrap gap-x-5 gap-y-1">
            <span>
              Views · <span className="font-semibold">{stats?.views ?? 0}</span>
            </span>
            {onBookingsClick ? (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onBookingsClick({
                    id,
                    title,
                    coverImage,
                    participationFee,
                    price,
                    duration,
                    format,
                  })
                }}
                className="cursor-pointer hover:opacity-80 transition-opacity"
              >
                Bookings · <span className="font-semibold">{stats?.bookings ?? 0}</span>
              </button>
            ) : (
              <span>
                Bookings · <span className="font-semibold">{stats?.bookings ?? 0}</span>
              </span>
            )}
            {onCompletedClick ? (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onCompletedClick({
                    id,
                    title,
                    coverImage,
                    participationFee,
                    price,
                    duration,
                    format,
                  })
                }}
                className="cursor-pointer hover:opacity-80 transition-opacity"
              >
                Completed · <span className="font-semibold">{stats?.completed ?? 0}</span>
              </button>
            ) : (
              <span>
                Completed · <span className="font-semibold">{stats?.completed ?? 0}</span>
              </span>
            )}
            {participationFee !== "Free" && (
              <span>
                Earnings · <span className="font-semibold">${stats?.earning ?? 0}</span>
              </span>
            )}
          </div>
        </div>
      )}
      {isOwner && (
        <div className="px-4 py-3.75 bg-[#F2F0EB]/10 shrink-0 block md:hidden">
          <div className="text-sm leading-normal text-[#F2F0EB]/60 flex flex-wrap gap-x-7.5 gap-y-1">
            <span className="flex items-center gap-2.5">
              <Eye className="w-5 h-5" />
              <span className="text-primary">{stats?.views ?? 0}</span>
            </span>
            {onBookingsClick ? (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onBookingsClick({
                    id,
                    title,
                    coverImage,
                    participationFee,
                    price,
                    duration,
                    format,
                  })
                }}
                className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <Users className="w-5 h-5" />{" "}
                <span className="text-primary">{stats?.bookings ?? 0}</span>
              </button>
            ) : (
              <span className="flex items-center gap-2.5">
                <Users className="w-5 h-5" />{" "}
                <span className="text-primary">{stats?.bookings ?? 0}</span>
              </span>
            )}
            {onCompletedClick ? (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onCompletedClick({
                    id,
                    title,
                    coverImage,
                    participationFee,
                    price,
                    duration,
                    format,
                  })
                }}
                className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <CircleCheck className="w-5 h-5" />{" "}
                <span className="text-primary">{stats?.completed ?? 0}</span>
              </button>
            ) : (
              <span className="flex items-center gap-2.5">
                <CircleCheck className="w-5 h-5" />{" "}
                <span className="text-primary">{stats?.completed ?? 0}</span>
              </span>
            )}
            {participationFee !== "Free" && (
              <span className="flex items-center gap-2.5">
                <Banknote className="w-5 h-5" />{" "}
                <span className="text-primary">${stats?.earning ?? 0}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {!isOwner && creator && (
        <CoachingBookSessionModal
          open={isBookingModalOpen}
          onOpenChange={setIsBookingModalOpen}
          session={{
            id: id,
            coverImage: coverImage ?? null,
            title: title,
            description: description ?? undefined,
            format: format as "IN_PERSON" | "VIRTUAL",
            duration: duration,
            durationType: durationType,
            price: price ?? null,
            participationFee: participationFee as "Free" | "Paid",
            websiteLink: websiteLink ?? null,
            tags: tags ?? [],
            streetAddress: location ?? null,
          }}
          expert={{
            id: creator.id,
            name: creator.name,
            username: creator.username,
            profilePictureUrl: creator.profilePictureUrl ?? null,
          }}
        />
      )}

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={
          typeof window !== "undefined" && creator?.id
            ? `${window.location.origin}/profile/${creator.id}?book=${slug || id}`
            : ""
        }
        shareText={`Check out this coaching session: ${title}`}
        shareType="coaching"
        shareImageUrl={coverImage || null}
        displayPath={`/coaching-sessions/${title.toLowerCase().replace(/\s+/g, "-")}`}
      />

      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
